import {useEffect, useRef} from 'react';

export const useSwiperWheelReleaseDelay = (
  swiperRef,
  enabled = true,
  options = {}
) => {
	const stateRef = useRef({
		locked: false,
		direction: null,
		enteredAt: 0,
		edgeReachedAt: 0,
	});

	const {
		activationDelay = 150,
		releaseDelay = 350,
		idleTimeout = 400,
		threshold = 4,
		catchMargin = 70,
	} = options;

	useEffect(() => {
		const swiper = swiperRef.current?.swiper;

		if (!enabled || !swiper || swiper.destroyed || !swiper.el) {
			return;
		}

		const el = swiper.el;
		let lastWheelTime = 0;

		const reset = () => {
			stateRef.current = {
				locked: false,
				direction: null,
				enteredAt: 0,
				edgeReachedAt: 0,
			};
		};

		const getViewportPosition = (scrollSpeed) => {
			const rect = el.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			const isApproaching = rect.top <= viewportHeight + catchMargin && rect.bottom >= -catchMargin;

			// Center of slider and center of viewport
			const sliderCenter = rect.top + rect.height / 2;
			const viewportCenter = viewportHeight / 2;
			const distanceFromCenter = Math.abs(sliderCenter - viewportCenter);

			// Adaptive threshold based on speed
			const minDistance = 50;
			const maxDistance = 300;
			const speedFactor = Math.min(scrollSpeed / 100, 1);  // беше 150
			const allowedDistance = minDistance + (speedFactor * (maxDistance - minDistance));

			const isVisible = distanceFromCenter < allowedDistance;

			return {isApproaching, isVisible, distanceFromCenter};
		};

		const handleWheel = (e) => {
			const absY = Math.abs(e.deltaY);
			const absX = Math.abs(e.deltaX);

			if (absY < threshold && absX < threshold) {
				return;
			}

			const now = Date.now();
			const state = stateRef.current;
			const scrollSpeed = Math.max(absY, absX);
			const {isApproaching, isVisible} = getViewportPosition(scrollSpeed);

			if (now - lastWheelTime > idleTimeout) {
				reset();
			}
			lastWheelTime = now;

			if (!isApproaching) {
				reset();
				return;
			}

			const isVertical = absY >= absX;
			const delta = isVertical ? e.deltaY : e.deltaX;
			const direction = delta > 0 ? 'forward' : 'backward';

			const isAtStart = swiper.isBeginning;
			const isAtEnd = swiper.isEnd;

			const atEdge =
			  (isAtStart && direction === 'backward') ||
			  (isAtEnd && direction === 'forward');

			if (!state.locked) {
				if (atEdge) {
					return;
				}

				if (!isVisible) {
					return;
				}

				state.locked = true;
				state.direction = direction;
				state.enteredAt = now;
				state.edgeReachedAt = 0;
			}

			if (state.locked && !isVisible) {
				reset();
				return;
			}

			if (direction !== state.direction) {
				state.direction = direction;
				state.edgeReachedAt = 0;
			}

			if (atEdge) {
				if (state.edgeReachedAt === 0) {
					state.edgeReachedAt = now;
				}

				if (now - state.edgeReachedAt >= releaseDelay) {
					reset();
					return;
				}

				e.preventDefault();
				return;
			}

			state.edgeReachedAt = 0;

			e.preventDefault();

			if (now - state.enteredAt < activationDelay) {
				return;
			}

			if (isVertical && !swiper.animating) {
				if (direction === 'forward') {
					swiper.slideNext();
				} else {
					swiper.slidePrev();
				}
			}
		};

		// Window-level listener is needed to catch fast scrolling
		// before the slider is in the viewport. Early exit with isApproaching
		// minimizes performance impact.
		window.addEventListener('wheel', handleWheel, {passive: false, capture: true});

		return () => {
			window.removeEventListener('wheel', handleWheel, {capture: true});
		};
	}, [swiperRef, enabled, activationDelay, releaseDelay, idleTimeout, threshold, catchMargin]);
};